import { useState, useRef } from "react";
import { Camera, Upload, Spinner, Lock, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { getVerdictColor } from "@/lib/utils-app";
import { processImageFile } from "@/lib/image-utils";
import api from "@/lib/api";

export default function DemoScanner() {
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setProcessing(true);
    const result = await processImageFile(file, 1024, 0.8);
    setImagePreview(result.preview);
    setImageBase64(result.base64);
    setVerdict(null);
    setError("");
    setProcessing(false);
  };

  const handleAnalyze = async () => {
    if (!imageBase64) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/demo/verdict", { image_base64: imageBase64 });
      setVerdict(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't analyze that image. Try a clearer photo of the item.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setImagePreview(null);
    setImageBase64(null);
    setVerdict(null);
    setError("");
  };

  if (verdict) {
    const colors = getVerdictColor(verdict.verdict);
    return (
      <div className="space-y-4" data-testid="demo-verdict-result">
        <div className="border-4 border-black bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] overflow-hidden">
          <div className="p-5 border-b-4 border-black" style={{ backgroundColor: colors.bg, color: colors.text }}>
            <div className="font-heading text-5xl font-black tracking-tighter uppercase">
              {verdict.verdict}
            </div>
            {verdict.override_reason && (
              <div className="mt-1 font-body text-sm font-medium opacity-80">{verdict.override_reason}</div>
            )}
          </div>
          <div className="p-4 border-b-2 border-black">
            <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500 mb-1">Listing Title</div>
            <div className="font-heading text-lg font-bold tracking-tight">{verdict.title_keywords}</div>
          </div>

          {/* Blurred fields */}
          <div className="grid grid-cols-2 relative">
            <div className="border-2 border-black p-3">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">List Price</div>
              <div className="font-heading text-lg font-bold blur-md select-none">$18-$28</div>
            </div>
            <div className="border-2 border-black p-3">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">Best Platform</div>
              <div className="font-heading text-lg font-bold blur-md select-none">Poshmark</div>
            </div>
            <div className="border-2 border-black p-3">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">Sold Comps</div>
              <div className="font-heading text-lg font-bold blur-md select-none">$12-$28</div>
            </div>
            <div className="border-2 border-black p-3">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">Time to Sell</div>
              <div className="font-heading text-lg font-bold blur-md select-none">7-14 days</div>
            </div>
            <div className="border-2 border-black p-3">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">Demand</div>
              <div className="font-heading text-lg font-bold blur-md select-none">High</div>
            </div>
            <div className="border-2 border-black p-3">
              <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">Season</div>
              <div className="font-heading text-lg font-bold blur-md select-none">Year-round</div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <div className="flex items-center gap-2 bg-black text-white px-4 py-2 border-2 border-black shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
                <Lock size={16} weight="bold" />
                <span className="font-heading font-bold text-sm uppercase tracking-wide">Sign up to unlock</span>
              </div>
            </div>
          </div>

          <Link
            to="/register"
            className="block w-full p-4 bg-[#00E676] text-black text-center font-heading font-black text-lg uppercase tracking-wide border-t-4 border-black active:translate-y-[1px] transition-transform"
            data-testid="demo-signup-cta"
          >
            <span className="flex items-center justify-center gap-2">
              Get Full Verdict Free
              <ArrowRight size={20} weight="bold" />
            </span>
          </Link>
        </div>
        <button
          onClick={handleReset}
          className="w-full p-3 border-2 border-black font-body font-semibold text-sm uppercase tracking-[0.1em] hover:bg-neutral-100"
          data-testid="demo-try-another"
        >
          Try Another Item
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="demo-scanner">
      {!imageBase64 ? (
        <div className="border-4 border-dashed border-black p-8 flex flex-col items-center justify-center min-h-[200px] gap-4 bg-white">
          <div className="font-heading text-lg font-bold tracking-tight text-center uppercase">
            {processing ? "Converting photo..." : "Try it now — no account needed"}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => cameraInputRef.current?.click()}
              className="bg-[#002FA7] text-white px-5 py-3 border-2 border-black shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all flex items-center gap-2 font-heading font-bold uppercase tracking-wide text-sm"
              data-testid="demo-camera-btn"
            >
              <Camera size={20} weight="bold" /> Photo
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-white text-black px-5 py-3 border-2 border-black shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all flex items-center gap-2 font-heading font-bold uppercase tracking-wide text-sm"
              data-testid="demo-upload-btn"
            >
              <Upload size={20} weight="bold" /> Upload
            </button>
          </div>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden"
            onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="border-4 border-black overflow-hidden shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
            {imagePreview ? (
              <img src={imagePreview} alt="Preview" className="w-full h-auto max-h-[250px] object-contain bg-neutral-100" />
            ) : (
              <div className="w-full h-[150px] bg-neutral-200 flex items-center justify-center">
                <span className="font-body text-sm text-neutral-500">Photo ready for analysis</span>
              </div>
            )}
          </div>
          {error && (
            <div className="border-2 border-black bg-[#FF1744] text-white p-3 font-body text-sm">{error}</div>
          )}
          <button
            onClick={handleAnalyze}
            disabled={loading}
            className="w-full bg-[#00E676] text-black p-4 font-heading font-black text-lg uppercase tracking-wide border-4 border-black shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:shadow-none transition-all disabled:opacity-50 flex items-center justify-center gap-3"
            data-testid="demo-analyze-btn"
          >
            {loading ? (
              <><Spinner size={24} weight="bold" className="animate-spin" /> Analyzing...</>
            ) : (
              "Get Free Verdict"
            )}
          </button>
        </div>
      )}
    </div>
  );
}
